// FieldVision - Dashboard.tsx - v2.0
// AENDRING: "Mine marker" navigerer nu til 'mineMarkerOversigt'
// AENDRING 2026-01-02: Bruger nu MarkerMap i stedet for MapView

import React, { useState, useEffect, useMemo } from 'react';
import MarkerMap from './MarkerMap';
import PricingModal from './PricingModal';
import apiService from '../services/api';

interface Order {
  orderId: number;
  orderNumber: string;
  createdAt: string;
  serviceType: string;
  source: string;
  status: string;
  fieldCount: number;
  fieldNames: string[];
}

interface DashboardProps {
  user: {
    userId: number;
    email: string;
    name: string;
  };
  // AENDRET: Tilfojet 'mineMarkerOversigt' som valid navigation
  onNavigate: (view: 'myFields' | 'booking' | 'mineMarkerOversigt') => void;
  onNavigateToFieldDetail?: (fieldId: number, fieldData?: any) => void;
  currentCVR: string;
  selectedFieldIds?: string[];
  zoomLoadedFields?: any[];
}

// Helper function to format field names for display
const formatFieldNames = (fieldNames: string[], maxDisplay: number = 3): string => {
  if (!fieldNames || fieldNames.length === 0) {
    return 'Ingen marker';
  }

  const formattedNames = fieldNames.map(name => {
    const trimmed = name.trim();
    if (/^\d+(-\d+)?$/.test(trimmed)) {
      return `Mark ${trimmed}`;
    }
    return trimmed;
  });

  if (formattedNames.length <= maxDisplay) {
    return formattedNames.join(', ');
  }

  const displayed = formattedNames.slice(0, maxDisplay);
  const remaining = formattedNames.length - maxDisplay;
  return `${displayed.join(', ')} +${remaining} mere`;
};

const Dashboard: React.FC<DashboardProps> = ({
  user,
  onNavigate,
  onNavigateToFieldDetail,
  currentCVR,
  selectedFieldIds = [],
  zoomLoadedFields = []
}) => {
  const [geojson, setGeojson] = useState<any>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Konverter geojson til fields format for MarkerMap
  const dashboardFields = useMemo(() => {
    if (!geojson?.features) return [];

    // CVR marker (filtreret til valgte)
    const cvrFields = geojson.features
      .filter((f: any) => selectedFieldIds.includes(`${f.properties.CVR}-${f.properties.Marknr}`))
      .map((f: any) => ({
        Id: f.properties.Id,
        Marknr: f.properties.Marknr,
        CVR: f.properties.CVR,
        IMK_areal: f.properties.IMK_areal,
        Geometry: JSON.stringify(f.geometry),
        isZoomLoaded: false
      }));

    // Tilfoej zoomLoadedFields hvis de findes - konverter til Field format
    const zoomFields = (zoomLoadedFields || []).map((f: any) => {
      const props = f.properties || f;
      return {
        Id: props.Id || props.id || Math.floor(Math.random() * 100000),
        Marknr: props.Marknr || props.marknr || '',
        CVR: props.CVR || props.cvr || '',
        IMK_areal: props.IMK_areal || props.GEOMETRISK || props.areal || 0,
        Geometry: f.geometry ? JSON.stringify(f.geometry) : props.Geometry,
        isZoomLoaded: true
      };
    });

    return [...cvrFields, ...zoomFields];
  }, [geojson, selectedFieldIds, zoomLoadedFields]);

  // Handle klik på mark i kortet - navigér til mark detaljer
  const handleFieldClick = (fieldKey: string) => {
    const field = dashboardFields.find((f: any) => `${f.CVR}-${f.Marknr}` === fieldKey);
    if (field && onNavigateToFieldDetail) {
      const fieldId = field.Id || 0;
      const navigationData = {
        marknr: field.Marknr,
        cvr: field.CVR,
        areal: field.IMK_areal,
        id: fieldId,
        geometry: field.Geometry
      };
      onNavigateToFieldDetail(fieldId, navigationData);
    }
  };

  // State for hover
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);

  useEffect(() => {
    loadRecentOrders();

    if (currentCVR && currentCVR.trim() !== '') {
      loadUserFields();
    } else {
      setGeojson(null);
    }
  }, [currentCVR]);

  const loadUserFields = async () => {
    try {
      setLoading(true);
      const cvrsToLoad = currentCVR.split(',').map(cvr => cvr.trim());
      console.log('Dashboard loading fields for selected CVRs:', cvrsToLoad);

      const fieldsData = await apiService.searchFields(cvrsToLoad);
      setGeojson(fieldsData);
    } catch (error) {
      console.error('Error loading fields:', error);
      setGeojson(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentOrders = async () => {
    try {
      const data = await apiService.getOrders();

      const formattedOrders = data.orders.map((order: any) => ({
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        createdAt: new Date(order.createdAt).toLocaleDateString('da-DK'),
        serviceType: order.serviceType,
        source: order.source || 'web',
        status: order.status === 'pending' ? 'Behandles' : order.status,
        fieldCount: order.fieldCount,
        fieldNames: order.fieldNames || []
      }));

      setOrders(formattedOrders.slice(0, 10));
    } catch (error) {
      console.error('Error loading orders:', error);
      setOrders([]);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Left Sidebar */}
      <div className="dashboard-sidebar">
        {/* Info Box */}
        <div className="info-box">
          <div
            className="info-header"
            onClick={() => setShowInfo(!showInfo)}
            style={{ cursor: 'pointer' }}
          >
            <div className="info-icon">i</div>
            <span>Hvad er tildelingskort og ukrudtskort</span>
            <span className="toggle-icon">{showInfo ? '▼' : '▶'}</span>
          </div>

          {showInfo && (
            <div className="info-content">
              <h3>Tildelingskort</h3>
              <p>
                Dansk Planteinspektion leverer tildelingskort til landbruget bygget på enten
                drone- eller satellitdata. Tildelingskortet er bygget på en fotosynteseanalyse
                af afgrøden og bruges efterfølgende til at tildele planteværn og kvælstof præcist
                der i afgrøden, hvor det kan gøre en forskel.
              </p>

              <h3>Ukrudtskort</h3>
              <p>
                Dansk Planteinspektion leverer ukrudtskort til landbruget bygget på dronebilleder,
                der kan bruges til at tildele planteværn præcist der, hvor det kan gøre en forskel.
              </p>
              <ul>
                <li>Spar op til 80% af planteværn.</li>
                <li>Minimer brugen af planteværn ved at bekæmpe præcist der, hvor der er uønsket vækst.</li>
                <li>Vi leverer bl.a. ukrudtskort, der fanger tidsler i korn og ukrudt i frøgræs.</li>
                <li>Med et nedvisningskort til kartofler behøver du kun at give anden og tredje behandling der, hvor der er behov for det</li>
              </ul>
            </div>
          )}
        </div>

        {/* Marker Section */}
        <div className="section-box">
          <h2>Marker</h2>
          <p className="section-description">Se og administrer dine marker</p>
          {/* AENDRET: Nu navigerer til 'mineMarkerOversigt' i stedet for 'myFields' */}
          <button
            className="btn-primary"
            onClick={() => onNavigate('mineMarkerOversigt')}
          >
            Mine marker
          </button>
        </div>

        {/* Tildelingskort Section */}
        <div className="section-box">
          <h2>Tildelingskort</h2>
          <p className="section-description">
            Bestil NDVI tildelingskort eller ukrudtskort baseret på drone- eller satellitbilleder
          </p>
          <div className="section-actions">
            <a
              href="#"
              className="link-secondary"
              onClick={(e) => {
                e.preventDefault();
                setShowPricingModal(true);
              }}
            >
              Se priser
            </a>
            <button
              className="btn-primary"
              onClick={() => onNavigate('booking')}
            >
              Bestil tildelingskort
            </button>
          </div>
        </div>

        {/* Recent Orders Table */}
        <div className="section-box">
          <h3>Seneste bestillinger</h3>
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>DATO</th>
                  <th>MARK</th>
                  <th>TYPE</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                      Ingen bestillinger endnu
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr
                      key={order.orderId}
                      onClick={() => onNavigateToFieldDetail && onNavigateToFieldDetail(order.orderId, order)}
                      style={{ cursor: 'pointer' }}
                      className="order-row"
                    >
                      <td>{order.createdAt}</td>
                      <td title={order.fieldNames.join(', ')}>
                        {formatFieldNames(order.fieldNames, 2)}
                      </td>
                      <td>{order.source === 'satellite' ? (
                        <><svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#333333" style={{verticalAlign: 'middle', marginRight: '4px'}}><path d="M560-32v-80q117 0 198.5-81.5T840-392h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T560-32Zm0-160v-80q50 0 85-35t35-85h80q0 83-58.5 141.5T560-192ZM222-57q-15 0-30-6t-27-17L23-222q-11-12-17-27t-6-30q0-16 6-30.5T23-335l127-127q23-23 57-23.5t57 22.5l50 50 28-28-50-50q-23-23-23-56t23-56l57-57q23-23 56.5-23t56.5 23l50 50 28-28-50-50q-23-23-23-56.5t23-56.5l127-127q12-12 27-18t30-6q15 0 29.5 6t26.5 18l142 142q12 11 17.5 25.5T895-730q0 15-5.5 30T872-673L745-546q-23 23-56.5 23T632-546l-50-50-28 28 50 50q23 23 22.5 56.5T603-405l-56 56q-23 23-56.5 23T434-349l-50-50-28 28 50 50q23 23 22.5 57T405-207L278-80q-11 11-25.5 17T222-57Zm0-79 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142Zm184-184 56-56-142-142-56 56 142 142Zm198-198 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142ZM448-504Z"/></svg>{order.serviceType}</>
                      ) : (
                        <><svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#333333" style={{verticalAlign: 'middle', marginRight: '4px'}}><path d="M160-160q0-75 41-134.5T307-381l-10-99H160v-240H40v-80h320v80H240v160h48l-8-80h400l-8 80h48v-160H600v-80h321v80H801v240H664l-10 99q65 27 105.5 86.5T800-160h-80q0-66-47-113t-113-47H400q-66 0-113 47t-47 113h-80Zm225-240h189l17-160H368l17 160Zm0 0h189-189Z"/></svg>{order.serviceType}</>
                      )}</td>
                      <td>{order.status}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Right Side - Map */}
      <div className="dashboard-map">
        {loading ? (
          <div className="loading-message">
            <p>Indlæser dine marker...</p>
          </div>
        ) : dashboardFields.length > 0 ? (
          <MarkerMap
            fields={dashboardFields}
            onFieldClick={handleFieldClick}
            selectedMarknr={null}
            onFieldHover={setHoveredFieldId}
          />
        ) : (
          <div className="empty-map-message">
            <p>{currentCVR ? 'Ingen marker fundet' : 'Vaelg dine marker for at se dem på kortet'}</p>
            <button
              className="btn-primary"
              onClick={() => onNavigate('mineMarkerOversigt')}
            >
              Mine marker
            </button>
          </div>
        )}
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />

      <style>{`
        .dashboard-container {
          display: grid;
          grid-template-columns: 400px 1fr;
          height: calc(100vh - 80px);
          gap: 0;
        }

        .dashboard-sidebar {
          background-color: #f5f5f5;
          padding: 20px;
          overflow-y: auto;
          border-right: 1px solid #ddd;
        }

        .dashboard-map {
          position: relative;
          height: 100%;
        }

        .info-box {
          background-color: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .info-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 15px;
          background-color: #f8f9fa;
          font-weight: 500;
        }

        .info-icon {
          background-color: #1976D2;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .toggle-icon {
          margin-left: auto;
          font-size: 12px;
        }

        .info-content {
          padding: 20px;
          font-size: 14px;
          line-height: 1.6;
          color: #666;
        }

        .info-content h3 {
          margin-top: 15px;
          margin-bottom: 10px;
          font-size: 16px;
          color: #333;
        }

        .info-content ul {
          margin-left: 20px;
          list-style-type: disc;
          padding-left: 20px;
        }

        .info-content li {
          margin: 5px 0;
        }

        .section-box {
          background-color: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .section-box h2 {
          font-size: 20px;
          margin-bottom: 10px;
          color: #333;
        }

        .section-box h3 {
          font-size: 18px;
          margin-bottom: 15px;
          color: #333;
        }

        .section-description {
          font-size: 14px;
          color: #666;
          margin-bottom: 15px;
          line-height: 1.5;
        }

        .section-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

/* btn-primary nu i global App.css */

        .link-secondary {
          color: #1976D2;
          text-decoration: none;
          font-size: 14px;
          text-align: center;
          padding: 8px;
        }

        .link-secondary:hover {
          text-decoration: underline;
        }

        .orders-table {
          overflow-x: auto;
        }

        .orders-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .orders-table th {
          background-color: #E8F5E9;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          color: #2E7D32;
          font-size: 12px;
        }

        .orders-table td {
          padding: 12px 10px;
          border-bottom: 1px solid #eee;
          color: #666;
        }

        .orders-table tbody tr:hover {
          background-color: #f5f5f5;
        }

        .loading-message,
        .empty-map-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #666;
        }

        .empty-map-message button {
          margin-top: 20px;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
